// Canvas-based ad renderer for Creative Maker v2.
// Renders REAL typography onto a property photo and returns a high-res PNG data URL.
// Supports 3 templates x 3 sizes. No external services — pure <canvas>.

export const AD_SIZES = {
  "1080x1080": { w: 1080, h: 1080, label: "Square", ratio: "1:1" },
  "1080x1350": { w: 1080, h: 1350, label: "Portrait", ratio: "4:5" },
  "1080x1920": { w: 1080, h: 1920, label: "Story", ratio: "9:16" },
};

export const TEMPLATES = ["Premium Listing", "Property Highlights", "Modern Minimal"];

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) return reject(new Error("no image"));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image failed to load"));
    img.src = src;
  });
}

function coverDraw(ctx, img, x, y, w, h) {
  const ar = img.width / img.height;
  const cr = w / h;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ar > cr) { sw = img.height * cr; sx = (img.width - sw) / 2; }
  else { sh = img.width / cr; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

// Word-wrap that returns the y AFTER the last line.
function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 4) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  if (lines.length > maxLines) shown[maxLines - 1] = shown[maxLines - 1].replace(/\s+\S*$/, "") + "…";
  shown.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineHeight));
  return y + shown.length * lineHeight;
}

// Wrap into an array of lines (with ellipsis on overflow) WITHOUT drawing.
function wrapLines(ctx, text, maxWidth, maxLines = 3) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = word; }
    else line = test;
  }
  if (line) lines.push(line);
  const shown = lines.slice(0, maxLines);
  if (lines.length > maxLines) shown[maxLines - 1] = shown[maxLines - 1].replace(/\s+\S*$/, "") + "…";
  return shown;
}

const FONT = "-apple-system, 'SF Pro Display', system-ui, 'Segoe UI', sans-serif";

// Draw the agent's brokerage logo as a subtle top-corner watermark chip so it
// appears on EVERY generated creative. Async (logo load); no-op if none.
async function drawLogoWatermark(ctx, logoSrc, W, H, s, corner = "tl") {
  if (!logoSrc) return;
  let logo;
  try { logo = await loadImage(logoSrc); } catch (_e) { return; }
  const pad = 40 * s;
  const maxH = 56 * s, maxW = 210 * s;
  const scale = Math.min(maxH / logo.height, maxW / logo.width);
  const lw = logo.width * scale, lh = logo.height * scale;
  const chipPadX = 16 * s, chipPadY = 12 * s;
  const cw = lw + chipPadX * 2, ch = lh + chipPadY * 2;
  const x = corner.includes("r") ? W - pad - cw : pad;
  const y = pad;
  ctx.save();
  roundRect(ctx, x, y, cw, ch, 14 * s);
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.shadowColor = "rgba(0,0,0,0.28)";
  ctx.shadowBlur = 18 * s;
  ctx.shadowOffsetY = 4 * s;
  ctx.fill();
  ctx.restore();
  ctx.drawImage(logo, x + chipPadX, y + chipPadY, lw, lh);
}


function pill(ctx, text, x, y, { bg, fg, size, padX = 22 * 1, padY = 14 }) {
  ctx.font = `700 ${size}px ${FONT}`;
  const w = ctx.measureText(text).width + padX * 2;
  const h = size + padY * 2;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + h / 2 + 1);
  ctx.textBaseline = "alphabetic";
  return { w, h };
}

/**
 * render an ad creative.
 * opts: { template, size, photo (dataURL), copy:{headline,subhead,cta,highlights[]},
 *         property:{price,beds,baths,sqft,address,city}, branding:{name,brokerage,phone,logo,primary,accent},
 *         priceVisible }
 * returns dataURL (png)
 */
export async function renderAd(opts) {
  const size = AD_SIZES[opts.size] || AD_SIZES["1080x1080"];
  const W = size.w, H = size.h;
  const s = W / 1080; // scale factor relative to 1080 base width
  const tall = H >= 1600; // story/reel formats get more vertical room
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const primary = opts.branding?.primary || "#1D6AFF";
  const accent = opts.branding?.accent || "#F59E0B";
  const copy = opts.copy || {};
  const prop = opts.property || {};
  const b = opts.branding || {};
  const priceVisible = opts.priceVisible !== false;

  // background
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, 0, W, H);
  let img = null;
  try { img = await loadImage(opts.photo); } catch (_e) { /* placeholder */ }

  const tpl = opts.template || "Premium Listing";

  if (tpl === "Property Highlights") {
    // Photo on top, solid brand panel on bottom with a measured, non-overlapping stack.
    const photoH = Math.round(H * (tall ? 0.6 : 0.56));
    if (img) coverDraw(ctx, img, 0, 0, W, photoH);
    else { ctx.fillStyle = "#1e293b"; ctx.fillRect(0, 0, W, photoH); }
    ctx.fillStyle = "#0B1220";
    ctx.fillRect(0, photoH, W, H - photoH);
    const fade = ctx.createLinearGradient(0, photoH - 120 * s, 0, photoH);
    fade.addColorStop(0, "rgba(11,18,32,0)");
    fade.addColorStop(1, "rgba(11,18,32,1)");
    ctx.fillStyle = fade;
    ctx.fillRect(0, photoH - 120 * s, W, 120 * s);

    const pad = 72 * s;
    const maxW = W - pad * 2;
    const ctaH = 68 * s;
    const bottomMargin = 70 * s;
    const actionTop = H - bottomMargin - ctaH;

    // agent brand (name / phone) top-right of the panel
    if (b.name || b.phone) {
      ctx.textAlign = "right";
      ctx.textBaseline = "alphabetic";
      if (b.name) { ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = `700 ${24 * s}px ${FONT}`; ctx.fillText(b.name, W - pad, photoH + 54 * s); }
      if (b.phone) { ctx.fillStyle = "rgba(255,255,255,0.72)"; ctx.font = `500 ${22 * s}px ${FONT}`; ctx.fillText(b.phone, W - pad, photoH + 84 * s); }
      ctx.textAlign = "left";
    }

    // top-down text stack
    let y = photoH + 58 * s;
    ctx.fillStyle = accent;
    ctx.fillRect(pad, y, 54 * s, 6 * s);
    y += 30 * s;
    ctx.textBaseline = "top";
    ctx.fillStyle = "#fff";
    const hSize = tall ? 56 * s : 48 * s;
    const hLH = hSize * 1.16;
    ctx.font = `800 ${hSize}px ${FONT}`;
    const hLines = wrapLines(ctx, copy.headline || prop.address || "Featured Listing", maxW, 2);
    hLines.forEach((ln, i) => ctx.fillText(ln, pad, y + i * hLH));
    y += hLines.length * hLH + 22 * s;

    // highlights — only as many as cleanly fit above the action row
    const hl = (copy.highlights || []).filter(Boolean);
    const rowH = 46 * s;
    const maxRows = Math.max(0, Math.floor((actionTop - 24 * s - y) / rowH));
    ctx.font = `500 ${29 * s}px ${FONT}`;
    hl.slice(0, Math.min(3, maxRows)).forEach((h) => {
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.arc(pad + 7 * s, y + 15 * s, 7 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      ctx.fillText(h, pad + 30 * s, y);
      y += rowH;
    });
    ctx.textBaseline = "alphabetic";

    // action row: CTA (left) + price (right)
    pill(ctx, (copy.cta || "Learn More").toUpperCase(), pad, actionTop, { bg: primary, fg: "#fff", size: 28 * s, padX: 32 * s, padY: 20 * s });
    if (priceVisible && prop.price) {
      ctx.font = `800 ${40 * s}px ${FONT}`;
      ctx.fillStyle = "#fff";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(prop.price, W - pad, actionTop + ctaH / 2 + 1);
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
    }
    await drawLogoWatermark(ctx, b.logo, W, H, s, "tl");
    return canvas.toDataURL("image/png");
  }

  if (tpl === "Modern Minimal") {
    // Full-bleed photo; lower text block measured and anchored from the bottom up.
    if (img) coverDraw(ctx, img, 0, 0, W, H);
    else { ctx.fillStyle = "#1e293b"; ctx.fillRect(0, 0, W, H); }
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "rgba(0,0,0,0.5)");
    g.addColorStop(0.4, "rgba(0,0,0,0.05)");
    g.addColorStop(0.66, "rgba(0,0,0,0.3)");
    g.addColorStop(1, "rgba(0,0,0,0.86)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    const pad = 72 * s;
    const maxW = W - pad * 2;
    // top row: brand (left) + price (right)
    if (!b.logo && (b.brokerage || b.name)) {
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.font = `700 ${24 * s}px ${FONT}`;
      ctx.fillText((b.brokerage || b.name).toUpperCase(), pad, 84 * s);
    }
    if (priceVisible && prop.price) {
      ctx.textAlign = "right";
      ctx.font = `800 ${30 * s}px ${FONT}`;
      ctx.fillStyle = "#fff";
      ctx.fillText(prop.price, W - pad, 84 * s);
      ctx.textAlign = "left";
    }

    // measure lower block
    const hSize = tall ? 66 * s : 52 * s;
    const hLH = hSize * 1.14;
    ctx.font = `800 ${hSize}px ${FONT}`;
    const hLines = wrapLines(ctx, copy.headline || "An Address You'll Love", maxW, tall ? 3 : 2);
    const subSize = 29 * s, subLH = 40 * s;
    let subLines = [];
    if (copy.subhead) { ctx.font = `500 ${subSize}px ${FONT}`; subLines = wrapLines(ctx, copy.subhead, maxW, 2); }
    const ctaH = 74 * s;
    const barH = 6 * s, eyebrowGap = 32 * s, afterHead = subLines.length ? 22 * s : 0, afterSub = 34 * s;
    const blockH = barH + eyebrowGap + hLines.length * hLH + (subLines.length ? afterHead + subLines.length * subLH : 0) + afterSub + ctaH;
    const bottomMargin = 100 * s;
    const topY = Math.max(pad, H - bottomMargin - blockH);

    ctx.fillStyle = accent;
    ctx.fillRect(pad, topY, 60 * s, barH);
    let cy = topY + barH + eyebrowGap;
    ctx.textBaseline = "top";
    ctx.fillStyle = "#fff";
    ctx.font = `800 ${hSize}px ${FONT}`;
    hLines.forEach((ln, i) => ctx.fillText(ln, pad, cy + i * hLH));
    cy += hLines.length * hLH;
    if (subLines.length) {
      cy += afterHead;
      ctx.font = `500 ${subSize}px ${FONT}`;
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      subLines.forEach((ln, i) => ctx.fillText(ln, pad, cy + i * subLH));
      cy += subLines.length * subLH;
    }
    cy += afterSub;
    ctx.textBaseline = "alphabetic";

    ctx.font = `700 ${28 * s}px ${FONT}`;
    const ctaText = copy.cta || "Learn More";
    const cw = ctx.measureText(ctaText).width + 64 * s;
    roundRect(ctx, pad, cy, cw, ctaH, ctaH / 2);
    ctx.fillStyle = primary;
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "middle";
    ctx.fillText(ctaText, pad + 32 * s, cy + ctaH / 2 + 1);
    ctx.textBaseline = "alphabetic";
    await drawLogoWatermark(ctx, b.logo, W, H, s, "tl");
    return canvas.toDataURL("image/png");
  }

  // DEFAULT: Premium Listing — hero photo, measured bottom stack, action row with brand bar.
  if (img) coverDraw(ctx, img, 0, 0, W, H);
  else { ctx.fillStyle = "#1e293b"; ctx.fillRect(0, 0, W, H); }
  const grad = ctx.createLinearGradient(0, H * 0.3, 0, H);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(0.55, "rgba(0,0,0,0.5)");
  grad.addColorStop(1, "rgba(0,0,0,0.92)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const pad = 72 * s;
  const maxW = W - pad * 2;
  if (prop.status) {
    pill(ctx, prop.status.toUpperCase(), pad, 64 * s, { bg: accent, fg: "#1a1204", size: 22 * s, padX: 22 * s, padY: 12 * s });
  }

  const ctaH = 68 * s;
  const bottomMargin = 68 * s;
  const actionTop = H - bottomMargin - ctaH;

  const priceSize = 64 * s;
  const hSize = 42 * s, hLH = 50 * s, specSize = 28 * s;
  ctx.font = `700 ${hSize}px ${FONT}`;
  const hLines = wrapLines(ctx, copy.headline || prop.address || "New Listing", maxW, 2);
  const specs = [];
  if (prop.beds) specs.push(`${prop.beds} Bd`);
  if (prop.baths) specs.push(`${prop.baths} Ba`);
  if (prop.sqft) specs.push(`${String(prop.sqft).replace(/[^\d,]/g, "")} sqft`);
  const priceH = (priceVisible && prop.price) ? priceSize : 0;
  const specsH = specs.length ? specSize : 0;
  const gPriceHead = 20 * s, gHeadSpecs = 16 * s, gBlockAction = 40 * s;
  const blockH = (priceH ? priceH + gPriceHead : 0) + hLines.length * hLH + (specsH ? gHeadSpecs + specsH : 0);
  let cy = Math.max(H * 0.3, actionTop - gBlockAction - blockH);

  ctx.textBaseline = "top";
  if (priceH) {
    ctx.fillStyle = "#fff";
    ctx.font = `800 ${priceSize}px ${FONT}`;
    ctx.fillText(prop.price, pad, cy);
    cy += priceSize + gPriceHead;
  }
  ctx.fillStyle = "#fff";
  ctx.font = `700 ${hSize}px ${FONT}`;
  hLines.forEach((ln, i) => ctx.fillText(ln, pad, cy + i * hLH));
  cy += hLines.length * hLH;
  if (specsH) {
    cy += gHeadSpecs;
    ctx.font = `500 ${specSize}px ${FONT}`;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText(specs.join("   •   "), pad, cy);
  }
  ctx.textBaseline = "alphabetic";

  pill(ctx, (copy.cta || "Book a Tour").toUpperCase(), pad, actionTop, { bg: primary, fg: "#fff", size: 28 * s, padX: 34 * s, padY: 20 * s });
  drawBrandBar(ctx, b, W, H, s, actionTop);
  await drawLogoWatermark(ctx, b.logo, W, H, s, "tr");
  return canvas.toDataURL("image/png");
}

// A slim agent brand strip (name + phone), right-aligned, within the action row.
function drawBrandBar(ctx, b, W, H, s, baseY) {
  const pad = 72 * s;
  const name = b.name || "";
  const phone = b.phone || "";
  if (!name && !phone) return;
  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  ctx.font = `700 ${24 * s}px ${FONT}`;
  if (name) ctx.fillText(name, W - pad, baseY + 28 * s);
  if (phone) {
    ctx.font = `500 ${22 * s}px ${FONT}`;
    ctx.fillStyle = "rgba(255,255,255,0.78)";
    ctx.fillText(phone, W - pad, baseY + 58 * s);
  }
  ctx.textAlign = "left";
}
